import { type PropsWithChildren } from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import excel from 'react-syntax-highlighter/dist/esm/languages/prism/excel-formula';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { Text } from '../Text';

SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('excelFormula', excel);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('tsx', tsx);

export type SyntaxLanguagage =
  | 'bash'
  | 'csv'
  | 'excel'
  | 'javascript'
  | 'json'
  | 'html'
  | 'markdown'
  | 'sql'
  | 'text'
  | 'typescript';

type Props = {
  title?: string;
  language?: SyntaxLanguagage | string | false;
};

const getSyntaxLanguage = (language: string) => {
  switch (language) {
    case 'bash':
    case 'csv':
    case 'html':
    case 'javascript':
    case 'json':
    case 'markdown':
    case 'sql':
    case 'text':
    case 'typescript':
      return language;
    case 'excel':
      return 'excelFormula';
    default:
      return undefined;
  }
};

const Code = ({ language, children }: PropsWithChildren<Props>) => {
  const syntaxLang = getSyntaxLanguage(language as string);

  return (
    <>
      {syntaxLang ? (
        <div className="overflow-hidden rounded-lg">
          <div className="rounded-t-lg bg-gray-900/60 px-2 py-0.5">
            <Text variant="caption1" color="white">
              {syntaxLang}
            </Text>
          </div>
          <SyntaxHighlighter
            className="m-0! rounded-t-none! rounded-b-lg! text-[0.85em]!"
            PreTag="div"
            language={syntaxLang}
            style={prism}
          >
            {String(children)}
          </SyntaxHighlighter>
        </div>
      ) : (
        <code className="m-0 rounded bg-white/80 px-[0.4em] py-[0.2em] text-[85%] whitespace-break-spaces">
          {children}
        </code>
      )}
    </>
  );
};

export default Code;
